import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiEye, FiEdit2, FiTrash2, FiPrinter, FiSearch } from 'react-icons/fi';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { immobilisationsAPI, categoriesAPI, servicesAPI, qrcodeAPI } from '../services/api';
import { toast } from 'react-toastify';
import ExportPDFButton from '../components/ExportPDFButton';

const ImmobilisationsPage = () => {
  const [immobilisations, setImmobilisations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterService, setFilterService] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });

  useEffect(() => {
    loadData();
  }, [page, searchTerm, filterCategory, filterState, filterService]);

  useEffect(() => {
    // Charger catégories et services une seule fois
    const loadFilters = async () => {
      try {
        const [catRes, servRes] = await Promise.all([
          categoriesAPI.getAll(),
          servicesAPI.getAll(),
        ]);
        setCategories(catRes.data.data || catRes.data || []);
        setServices(servRes.data.data || servRes.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    loadFilters();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (searchTerm) params.search = searchTerm;
      if (filterCategory) params.categorie_id = filterCategory;
      if (filterState) params.etat = filterState;
      if (filterService) params.service_id = filterService;

      const res = await immobilisationsAPI.getAll(params);
      // Backend retourne { data: [...], pagination: { page, limit, total, pages } }
      setImmobilisations(res.data.data || []);
      setTotalPages(res.data.pagination?.pages || 1);
      setTotal(res.data.pagination?.total || 0);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await immobilisationsAPI.delete(deleteModal.id);
      toast.success('Immobilisation supprimée');
      setDeleteModal({ isOpen: false, id: null });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handlePrintLabel = (immo) => {
    window.open(qrcodeAPI.label(immo.id), '_blank');
  };

  const getStateLabel = (etat) => {
    switch (etat) {
      case 'bon': return 'Bon';
      case 'moyen': return 'Moyen';
      case 'hors_service': return 'Hors service';
      default: return etat;
    }
  };

  const getStateBadge = (etat) => {
    const color = etat === 'bon' ? 'success' : etat === 'moyen' ? 'warning' : 'error';
    return <span className={`badge badge-${color}`}>{getStateLabel(etat)}</span>;
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="page-content" id="page-content">
          <div className="page-header">
            <h1 className="page-title">Immobilisations ({total})</h1>
            <ExportPDFButton title="OGP IMMO - Immobilisations" fileName="immobilisations-ogp-immo" orientation="landscape" />
            <Link to="/immobilisations/new" className="btn btn-secondary">
              <FiPlus /> Nouvelle
            </Link>
          </div>

          {/* Search & Filters */}
          <div className="card" style={{ marginBottom: '1.5rem', padding: '1.5rem' }}>
            <div className="search-bar" style={{ marginBottom: '1rem' }}>
              <FiSearch style={{ color: 'var(--text-light)' }} />
              <input
                type="text"
                placeholder="Rechercher par nom, numéro..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              />
            </div>

            <div className="filter-group" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div className="filter-item" style={{ flex: 1, minWidth: '150px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: 600 }}>Catégorie</label>
                <select value={filterCategory} onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}>
                  <option value="">Toutes</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.nom}</option>
                  ))}
                </select>
              </div>

              <div className="filter-item" style={{ flex: 1, minWidth: '150px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: 600 }}>État</label>
                <select value={filterState} onChange={(e) => { setFilterState(e.target.value); setPage(1); }}>
                  <option value="">Tous</option>
                  <option value="bon">Bon</option>
                  <option value="moyen">Moyen</option>
                  <option value="hors_service">Hors service</option>
                </select>
              </div>

              <div className="filter-item" style={{ flex: 1, minWidth: '150px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: 600 }}>Service</label>
                <select value={filterService} onChange={(e) => { setFilterService(e.target.value); setPage(1); }}>
                  <option value="">Tous</option>
                  {services.map((svc) => (
                    <option key={svc.id} value={svc.id}>{svc.nom}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card">
            {loading ? <LoadingSpinner /> : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Nom</th>
                        <th>N° Identification</th>
                        <th>Catégorie</th>
                        <th>Service</th>
                        <th>État</th>
                        <th>Enrôlé</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {immobilisations.length === 0 ? (
                        <tr>
                          <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
                            Aucune immobilisation trouvée
                          </td>
                        </tr>
                      ) : (
                        immobilisations.map((immo) => (
                          <tr key={immo.id}>
                            <td style={{ fontWeight: 600 }}>
                              <Link to={`/immobilisations/${immo.id}`} style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>
                                {immo.nom}
                              </Link>
                            </td>
                            <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{immo.numero_identification}</td>
                            <td>{immo.categorie_nom || '-'}</td>
                            <td>{immo.service_nom || '-'}</td>
                            <td>{getStateBadge(immo.etat)}</td>
                            <td>{immo.enrolle ? '\u2705' : '\u274C'}</td>
                            <td>
                              <div style={{ display: 'flex', gap: '0.25rem' }}>
                                <Link to={`/immobilisations/${immo.id}`} className="btn btn-primary btn-sm" title="Voir">
                                  <FiEye />
                                </Link>
                                <Link to={`/immobilisations/${immo.id}/edit`} className="btn btn-secondary btn-sm" title="Éditer">
                                  <FiEdit2 />
                                </Link>
                                <button onClick={() => handlePrintLabel(immo)} className="btn btn-sm" style={{ backgroundColor: '#243660', color: 'white' }} title="Imprimer QR">
                                  <FiPrinter />
                                </button>
                                <button onClick={() => setDeleteModal({ isOpen: true, id: immo.id })} className="btn btn-error btn-sm" title="Supprimer">
                                  <FiTrash2 />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', padding: '1rem 0' }}>
                    <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                      &#8592; Précédent
                    </button>
                    <span style={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem', color: 'var(--text-light)' }}>
                      Page {page} / {totalPages}
                    </span>
                    <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                      Suivant &#8594;
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Delete Confirmation Modal */}
          <Modal
            isOpen={deleteModal.isOpen}
            title="Confirmer la suppression"
            onClose={() => setDeleteModal({ isOpen: false, id: null })}
            footer={
              <>
                <button onClick={() => setDeleteModal({ isOpen: false, id: null })} className="btn btn-ghost">
                  Annuler
                </button>
                <button onClick={handleDelete} className="btn btn-error">
                  Supprimer
                </button>
              </>
            }
          >
            <p>Êtes-vous sûr de vouloir supprimer cette immobilisation ? Cette action est irréversible.</p>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default ImmobilisationsPage;
